// Inline quick sign-in modal. When some part of the app needs auth (leave
// a tip, save a photo, nominate, etc.) it can pop this open and pass an
// onSuccess callback to resume exactly where the user left off — no
// full-page redirect, no losing context.

import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { z } from "zod";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { signInWithProvider } from "@/utils/nativeAuth";
import { ConfirmEmailSent } from "@/components/ConfirmEmailSent";
import { toast } from "sonner";
import {
  isRepeatSignup,
  REPEAT_SIGNUP_DESCRIPTION,
  REPEAT_SIGNUP_TITLE,
} from "@/lib/repeatSignup";

const emailSchema = z.string().trim().email("Enter a valid email").max(255);
const passwordSchema = z.string().min(8, "At least 8 characters").max(72);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Headline shown above the buttons — describes what the user is about to do. */
  reason?: string;
  /** Called after a successful password sign-in. OAuth completes via redirect. */
  onSuccess: () => void;
}

export const QuickAuthDialog = ({ open, onOpenChange, reason, onSuccess }: Props) => {
  const [mode, setMode] = useState<"signin" | "signup">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  /** Persistent post-sign-up state - the form must not re-arm (Aug 20:
   *  double-taps retired working links; see ConfirmEmailSent.tsx). */
  const [confirmSent, setConfirmSent] = useState<{ email: string; redirect: string } | null>(null);

  const handleOAuth = async (provider: "google" | "apple") => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await signInWithProvider(provider);
      if (result.error) {
        const providerLabel = provider === "google" ? "Google" : "Apple";
        toast.error(`Couldn't sign in with ${providerLabel}`);
        setBusy(false);
      }
      // Web: full page redirect. Native: in-app browser opens; deep-link
      // handler in useDeepLinks completes the session and onSuccess is
      // resumed by the existing onAuthStateChange listener in this dialog.
    } catch {
      toast.error("Sign-in failed");
      setBusy(false);
    }
  };

  /** Mail a reset link (Morgan, Aug 3 — the app's own sign-in had no way to
   *  recover a password; users had to know the website existed). Mirrors
   *  Auth.tsx exactly: the link must be a real HTTPS URL because it lands in
   *  an inbox — the user resets in the browser, then signs in here. */
  const handleForgotPassword = async () => {
    if (busy) return;
    try {
      const parsedEmail = emailSchema.parse(email);
      setBusy(true);
      const isNative = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } })
        .Capacitor?.isNativePlatform?.() ?? false;
      const redirect = isNative
        ? "https://pleasantroutes.com/reset"
        : `${window.location.origin}/reset`;
      const { error } = await supabase.auth.resetPasswordForEmail(parsedEmail, {
        redirectTo: redirect,
      });
      if (error) throw error;
      // Same message whether or not the address has an account — otherwise
      // this becomes a way to discover who has one.
      toast.success("Check your email", {
        description: "If that address has an account, a reset link is on its way.",
      });
    } catch (err: unknown) {
      const msg =
        err instanceof z.ZodError
          ? "Enter your email address first"
          : err instanceof Error
          ? err.message
          : "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    try {
      const parsedEmail = emailSchema.parse(email);
      const parsedPassword = passwordSchema.parse(password);
      setBusy(true);
      if (mode === "signup") {
        // emailRedirectTo lands in the user's inbox and MUST be HTTPS,
        // not `capacitor://localhost` (window.location.origin inside the
        // native shell). Use the production origin on native.
        const isNative = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } })
          .Capacitor?.isNativePlatform?.() ?? false;
        // /confirmed = dedicated "you're confirmed, open the app" landing.
        const emailRedirect = isNative
          ? "https://pleasantroutes.com/confirmed"
          : `${window.location.origin}/confirmed`;
        const { data, error } = await supabase.auth.signUp({
          email: parsedEmail,
          password: parsedPassword,
          options: { emailRedirectTo: emailRedirect },
        });
        if (error) throw error;
        // Existing confirmed account: Supabase sent nothing, so don't say
        // "check your inbox". Flip to sign-in with the email kept.
        if (isRepeatSignup(data.user)) {
          setMode("signin");
          toast(REPEAT_SIGNUP_TITLE, { description: REPEAT_SIGNUP_DESCRIPTION, duration: 8000 });
          return;
        }
        // Persistent panel instead of a vanishing toast - see ConfirmEmailSent.
        setConfirmSent({ email: parsedEmail, redirect: emailRedirect });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: parsedEmail,
          password: parsedPassword,
        });
        if (error) throw error;
        toast.success("Welcome back");
        onOpenChange(false);
        onSuccess();
      }
    } catch (err: unknown) {
      const msg =
        err instanceof z.ZodError
          ? err.errors[0]?.message ?? "Check your input"
          : err instanceof Error
          ? err.message
          : "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle>Sign in to continue</DialogTitle>
          {reason && (
            <DialogDescription className="text-sm text-muted-foreground">
              {reason} You'll come right back here.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-2">
          {/* OAuth buttons hidden — provider credentials can't be configured
           *  on the current Lovable-owned Supabase project. Email works. */}
          {confirmSent ? (
            <ConfirmEmailSent
              email={confirmSent.email}
              emailRedirectTo={confirmSent.redirect}
              onChangeEmail={() => setConfirmSent(null)}
            />
          ) : !showEmail ? (
            <button
              type="button"
              onClick={() => setShowEmail(true)}
              className="w-full h-11 rounded-full bg-card border border-border flex items-center justify-center gap-2 text-sm font-medium hover:bg-muted/50 transition-smooth"
            >
              <Mail className="w-4 h-4" /> Continue with email
            </button>
          ) : (
            <form onSubmit={handleEmail} className="space-y-2 pt-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                required
                className="w-full h-11 px-4 rounded-full bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (8+ characters)"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                required
                minLength={8}
                className="w-full h-11 px-4 rounded-full bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-full bg-pleasant text-primary-foreground text-sm font-semibold shadow-glow hover:brightness-110 transition-smooth disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "signup" ? "Create account" : "Sign in"}
              </button>
              {mode === "signin" && (
                <p className="text-xs text-center pt-0.5">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={busy}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    Forgot your password?
                  </button>
                </p>
              )}
              <p className="text-xs text-center text-muted-foreground pt-1">
                {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
                <button
                  type="button"
                  onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
                  className="text-primary hover:underline font-medium"
                >
                  {mode === "signup" ? "Sign in" : "Create one"}
                </button>
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M23 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.16c-.27 1.4-1.07 2.59-2.28 3.39v2.82h3.69C21.7 18.71 23 15.77 23 12.27z"/>
    <path fill="#34A853" d="M12 23c3.08 0 5.66-1.02 7.55-2.78l-3.69-2.82c-1.02.69-2.33 1.1-3.86 1.1-2.97 0-5.49-2-6.39-4.7H1.83v2.95C3.71 20.45 7.56 23 12 23z"/>
    <path fill="#FBBC05" d="M5.61 13.8c-.23-.69-.36-1.43-.36-2.2s.13-1.51.36-2.2V6.45H1.83C1.05 7.97.6 9.69.6 11.5s.45 3.53 1.23 5.05l3.78-2.95z"/>
    <path fill="#EA4335" d="M12 5.5c1.68 0 3.18.58 4.36 1.71l3.27-3.27C17.65 2.04 15.07 1 12 1 7.56 1 3.71 3.55 1.83 7.45l3.78 2.95C6.51 7.5 9.03 5.5 12 5.5z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden fill="currentColor">
    <path d="M16.365 12.84c-.024-2.43 1.987-3.604 2.077-3.66-1.131-1.652-2.892-1.879-3.518-1.905-1.498-.151-2.927.882-3.69.882-.766 0-1.94-.86-3.193-.836-1.643.024-3.157.954-4.001 2.422-1.706 2.957-.436 7.337 1.227 9.74.81 1.171 1.776 2.487 3.045 2.44 1.222-.05 1.683-.79 3.16-.79 1.476 0 1.892.79 3.184.764 1.314-.024 2.146-1.193 2.95-2.37.93-1.36 1.314-2.677 1.337-2.745-.029-.013-2.567-.985-2.578-3.942zM13.79 5.49c.676-.82 1.131-1.96.99-3.106-.957.04-2.118.638-2.815 1.453-.625.72-1.173 1.872-1.025 2.99 1.07.083 2.165-.541 2.85-1.337z"/>
  </svg>
);
